
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Download, 
  Mail, 
  Calendar,
  ExternalLink
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { toast } from 'sonner';
import { AssessmentResult } from '@/types/assessment';
import { downloadStoredPdf } from '@/utils/storage/pdfStorageService';
import LoadingOverlay from '@/components/assessment/LoadingOverlay';
import { useState } from 'react';

interface ActionCardsProps {
  result?: AssessmentResult | null;
  userEmail?: string;
  packageType?: string | null;
}

const ActionCards = ({ result, userEmail, packageType }: ActionCardsProps) => {
  const { toast: uiToast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleDownloadPDF = async () => {
    if (!result) {
      toast.error("Report not available yet. Please try again later.");
      return;
    }
    
    try {
      // If PDF URL is already present, open it in a new tab instead of regenerating
      if (result.pdfUrl) {
        window.open(result.pdfUrl, '_blank');
        return;
      }
      
      // Show processing overlay
      setIsProcessing(true);
      
      // Use the downloadStoredPdf function
      const success = await downloadStoredPdf(result);
      
      if (!success) {
        toast.error("PDF generation failed. Please try again later.");
      }
    } catch (error) {
      toast.error("Error generating PDF report. Please try again.");
    } finally {
      // Hide processing overlay when done (whether successful or not)
      setIsProcessing(false);
    }
  };

  // Determine if we should show the expert session card (only for gold packages)
  const showExpertSession = packageType !== 'silver';

  return (
    <>
      <LoadingOverlay 
        isVisible={isProcessing} 
        message="Your assessment result is preparing in PDF format to download..." 
      />
      
      <div className={`grid grid-cols-1 ${showExpertSession ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6 mb-8`}>
        <Card className="shadow-md hover:shadow-lg transition-shadow">
          <CardContent className="p-6 flex flex-col items-center text-center">
            {result?.pdfUrl ? (
              <ExternalLink className="h-10 w-10 text-brand-blue mb-4" />
            ) : (
              <Download className="h-10 w-10 text-brand-blue mb-4" />
            )}
            <h3 className="text-lg font-semibold mb-2">Download Report</h3>
            <p className="text-gray-500 text-sm mb-4">
              {result?.pdfUrl 
                ? "View your personalized career guidance report." 
                : "Get a copy of your personalized career guidance report."}
            </p>
            <Button 
              onClick={handleDownloadPDF}
              className="mt-auto bg-brand-blue hover:bg-brand-blue/90"
            >
              {result?.pdfUrl ? "View PDF Report" : "Download PDF"}
            </Button>
          </CardContent>
        </Card>
        
        <Card className="shadow-md hover:shadow-lg transition-shadow">
          <CardContent className="p-6 flex flex-col items-center text-center">
            <Mail className="h-10 w-10 text-brand-blue mb-4" />
            <h3 className="text-lg font-semibold mb-2">Email Report</h3>
            <p className="text-gray-500 text-sm mb-4">
              A copy has been sent to your registered email address.
            </p>
            <div className="mt-auto text-sm text-gray-500 bg-gray-50 py-2 px-4 rounded-full">
              {userEmail || 'No email available'}
            </div>
          </CardContent>
        </Card>
        
        {showExpertSession && (
          <Card className="shadow-md hover:shadow-lg transition-shadow">
            <CardContent className="p-6 flex flex-col items-center text-center">
              <Calendar className="h-10 w-10 text-brand-blue mb-4" />
              <h3 className="text-lg font-semibold mb-2">Expert Session</h3>
              <p className="text-gray-500 text-sm mb-4">
                Your free session with our career counselor will be scheduled soon.
              </p>
              <div className="mt-auto text-sm bg-yellow-100 text-yellow-800 py-2 px-4 rounded-full">
                Pending Scheduling
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
};

export default ActionCards;
